import React from "react";
import { Button, Checkbox, HStack, Text } from "@chakra-ui/react";

/**
 * The unified selection bar shown above Grid/List views once Bulk Edit
 * selection is possible: "Select all N loaded" (Grid only — List has its own
 * header checkbox), the live selection count, Clear Selection, and the
 * escalation link/banner for "select all matching records" vs the
 * already-selected "allFiltered" state.
 *
 * Shared by every *Table.js page that wires up Bulk Edit (Library, Archive,
 * ...) — `selection` is a useBulkSelection() instance and `records`/
 * `totalRecords` describe the currently loaded page, exactly as each page
 * already tracks them.
 */
const SelectionToolbar = ({ viewMode, records, totalRecords, selection }) => {
  if (!((viewMode === "grid" && records.length > 0) || selection.count > 0)) {
    return null;
  }

  return (
    <HStack
      px={4}
      py={2}
      bg={selection.count > 0 ? "blue.50" : "gray.50"}
      borderBottom="1px solid"
      borderColor={selection.count > 0 ? "blue.100" : "gray.100"}
      spacing={4}
    >
      {viewMode === "grid" &&
        (() => {
          const loadedIds = records.map((r) => r?._id).filter(Boolean);
          const allLoadedSelected =
            loadedIds.length > 0 && loadedIds.every((id) => selection.isSelected(id));
          const someLoadedSelected = loadedIds.some((id) => selection.isSelected(id));
          return (
            <Checkbox
              isChecked={allLoadedSelected}
              isIndeterminate={someLoadedSelected && !allLoadedSelected}
              onChange={(e) => selection.selectMany(loadedIds, e.target.checked)}
            >
              <Text fontSize="13px" fontWeight="600" color="gray.600">
                Select all {loadedIds.length} loaded
              </Text>
            </Checkbox>
          );
        })()}

      {selection.count > 0 && (
        <>
          <Text fontSize="14px" fontWeight="600">
            {selection.count} selected
          </Text>
          <Button size="sm" variant="ghost" onClick={selection.clear}>
            Clear Selection
          </Button>
          {selection.mode === "ids" &&
            records.length > 0 &&
            selection.selectedIds.length >= records.length &&
            totalRecords > records.length && (
              <Button size="sm" variant="link" onClick={() => selection.selectAllFiltered(totalRecords)}>
                Select all {totalRecords} records
              </Button>
            )}
          {selection.mode === "allFiltered" && (
            <Text fontSize="13px" color="blue.700">
              All {selection.totalFiltered} matching records selected
            </Text>
          )}
        </>
      )}
    </HStack>
  );
};

export default SelectionToolbar;
